import { DeleteRounded, ExpandMore, Person, SearchRounded } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Grid, IconButton, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useContext, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
    CreditType,
    FirstInvoiceRecipient,
    ICustomer,
    IMobilePrefix,
    InvoiceType,
    ReactQueryKey,
    RentalType
} from "../../../../../types";
import {getCountries, getMobilePrefixes, IGetCompanyReturn, IGetCustomersParams} from "../../../../../api/services";
import { IFormValues } from "../../../types";
import { FormAutocomplete, FormDatePicker, FormTextField } from "../../../../../components/fields";
import { GlobalContext } from "../../../../../../../util/state/GlobalContextProvider";
import { editAccordionExpandedState, editPendingBookingRce } from "../../../recoil";
import useDialog from "../../../../../hooks/useDialog";
import { setSnackbar } from "../../../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../../../util/state/globalVariables";
import DialogSearchCustomer from "../../../../../components/DialogSearchCustomer";
import GoogleAutocompleteWrapper from "../../../../../../../util/component/GoogleAutocompleteWrapper";
import { clearFieldInvoice, setInvoicePropertyPersonCustomer } from "../../../../../utils";
import { useRecoilValue } from "recoil";

const BirthDateField = () => {

    const { t } = useTranslation('booking');

    const firstDriverId = useWatch<IFormValues, 'firstDriver.id'>({
        name: 'firstDriver.id'
    });
    const birthDate = useWatch<IFormValues, 'firstDriver.birthDate'>({
        name: 'firstDriver.birthDate'
    });
    const rentalType = useWatch<IFormValues, 'basicDetails.rentalType'>({
        name: 'basicDetails.rentalType'
    });

    return (
        <FormDatePicker<IFormValues>
            name="firstDriver.birthDate"
            label={t('label.dateOfBirth')}
            disableFuture
            textFieldProps={{
                required: rentalType === RentalType.RETAIL,
                helperText: dayjs().diff(birthDate, 'y') < 24 ? t('label.youngDriver') : undefined
            }}
            disabled={!!firstDriverId}
        />
    )
}

const MobilePrefixField = (props: { isPendingBookingRce: boolean | undefined }) => {

    const { t } = useTranslation('booking');

    const rentalType = useWatch<IFormValues, 'basicDetails.rentalType'>({
        name: 'basicDetails.rentalType'
    });

    const mobilePrefixesQuery = useQuery({
        queryKey: [ReactQueryKey.MobilePrefixes],
        queryFn: getMobilePrefixes
    });

    return (
        <FormAutocomplete<IFormValues>
            name="firstDriver.mobilePrefix"
            label={t('label.prefix')}
            disabled={props.isPendingBookingRce}
            options={mobilePrefixesQuery.data ?? []}
            isOptionEqualToValue={(option, value) => option.country === value.country}
            getOptionLabel={(option) => ('+' + option.prefix + ` (${option.country})`)}
            textFieldProps={{
                required: rentalType === RentalType.RETAIL
            }}
        />
    )
}

const BirthNationField = (props: any) => {

    const { t } = useTranslation('booking');

    const countriesQuery = useQuery({
        queryKey: [ReactQueryKey.Countries],
        queryFn: getCountries
    });

    const firstDriverId = useWatch<IFormValues, 'firstDriver.id'>({
        name: 'firstDriver.id'
    });

    return (
        <FormAutocomplete<IFormValues>
            name="firstDriver.birthNation"
            label={t('label.countryOfBirth')}
            options={countriesQuery.data ?? []}
            disabled={!!firstDriverId || props.birthNationDisabled}
        />
    );
}

const ResidenceCountryField = () => {

    const { t } = useTranslation('booking');

    const countriesQuery = useQuery({
        queryKey: [ReactQueryKey.Countries],
        queryFn: getCountries
    });

    const firstDriverId = useWatch<IFormValues, 'firstDriver.id'>({
        name: 'firstDriver.id'
    });

    return (
        <FormAutocomplete<IFormValues>
            name="firstDriver.residenceCountry"
            label={t('label.countryOfResidence')}
            options={countriesQuery.data ?? []}
            disabled={!!firstDriverId}
        />
    );
}

const FirstDriverDetails = () => {

    const isPendingBookingRce = useRecoilValue<boolean | undefined>(editPendingBookingRce);

    const { dispatchGlobalData } = useContext(GlobalContext);

    const { t } = useTranslation('booking');

    const queryClient = useQueryClient();

    const [firstDriverExpanded, setFirstDriverExpanded] = useRecoilState(editAccordionExpandedState('firstDriver'));

    const formContext = useFormContext<IFormValues>();

    const firstDriverId = useWatch<IFormValues, 'firstDriver.id'>({
        name: 'firstDriver.id'
    });
    const firstDriverUUID = useWatch<IFormValues, 'firstDriver.uuid'>({
        name: 'firstDriver.uuid'
    });

    const birthNation = useWatch<IFormValues, "firstDriver.birthNation">({
        name: 'firstDriver.birthNation'
    });

    const rentalType = useWatch<IFormValues, 'basicDetails.rentalType'>({
        name: 'basicDetails.rentalType'
    });

    const [firstDriverFilter, setFirstDriverFilter] = useState<IGetCustomersParams['filters'] | null>(null);
    const [birthNationDisabled, setBirthNationDisabled] = useState<boolean>(!!birthNation);

    const searchDialog = useDialog();

    const handleSearchClick = useCallback((e: React.SyntheticEvent) => {
        e.stopPropagation();
        const firstDriver = formContext.getValues('firstDriver');
        if (firstDriver.birthDate && (!firstDriver.name || !firstDriver.surname)) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.first_driver_search_required_fields'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            })
            return;
        }
        setFirstDriverFilter({
            firstName: firstDriver.name,
            lastName: firstDriver.surname,
            birthDate: firstDriver.birthDate?.format('YYYY-MM-DD') || '',
        });
        searchDialog.handleShow();
    }, []);
    const handleCustomerSelected = useCallback((selectedCustomer: ICustomer) => {
        formContext.setValue('firstDriver.id', selectedCustomer.customerId);
        formContext.setValue('firstDriver.name', selectedCustomer.firstName);
        formContext.setValue('firstDriver.surname', selectedCustomer.lastName);
        formContext.setValue('firstDriver.birthDate', selectedCustomer.birthDate !== '' ? dayjs(selectedCustomer.birthDate, 'YYYY-MM-DD') : null);
        formContext.setValue('firstDriver.birthNation', selectedCustomer.birthNation);
        formContext.setValue('firstDriver.birthPlace', selectedCustomer.birthPlace);
        formContext.setValue('firstDriver.residenceCountry', selectedCustomer.residenceCountry);
        formContext.setValue('firstDriver.fiscalCode', selectedCustomer.fiscalCode);
        formContext.setValue('firstDriver.drivingLicenseNumber', selectedCustomer.drivingLicenseNumber);
        formContext.setValue('firstDriver.mobile', selectedCustomer.mobile);
        formContext.setValue('firstDriver.email', selectedCustomer.email);
        formContext.setValue('firstDriver.uuid', selectedCustomer.uuid);
        formContext.setValue('firstDriver.gender', selectedCustomer.gender);

        const mobilePrefixes = queryClient.getQueryData<IMobilePrefix[]>([ReactQueryKey.MobilePrefixes]);
        const foundMobilePrefix = mobilePrefixes?.find(mp => mp.prefix === selectedCustomer.mobilePrefix);
        formContext.setValue('firstDriver.mobilePrefix', foundMobilePrefix ?? null);

        let productAffiliatedQuery;
        if(rentalType === RentalType.CORPORATE) {
            productAffiliatedQuery = queryClient.getQueryData<IGetCompanyReturn>([ReactQueryKey.Product, {segment: rentalType}]);

            if (productAffiliatedQuery?.firstInvoiceRecipient !== FirstInvoiceRecipient.COMPANY &&
                productAffiliatedQuery?.creditType === CreditType.NO_CREDIT &&
                formContext.getValues('invoice.type') === InvoiceType.PERSON) {
                for (const [key, value] of Object.entries(setInvoicePropertyPersonCustomer(selectedCustomer))) {
                    // @ts-ignore
                    formContext.setValue((`${key}`), value);
                }
                formContext.setValue('invoice.mobilePrefix', foundMobilePrefix ?? null);
            }
        }
    }, []);

    /**
     * Clear form first driver
     */
    const handleDeleteClick = useCallback((e: React.SyntheticEvent) => {
        e.stopPropagation();
        formContext.setValue('firstDriver.id' , '');
        formContext.setValue('firstDriver.name' , '');
        formContext.setValue('firstDriver.surname' , '');
        formContext.setValue('firstDriver.birthDate' , null);
        formContext.setValue('firstDriver.birthNation' , '');
        formContext.setValue('firstDriver.birthPlace' , '');
        formContext.setValue('firstDriver.residenceCountry' , '');
        formContext.setValue('firstDriver.fiscalCode' , '');
        formContext.setValue('firstDriver.drivingLicenseNumber' , '');
        formContext.setValue('firstDriver.email' , '');
        formContext.setValue('firstDriver.uuid' , '');
        formContext.setValue('firstDriver.mobilePrefix' , null);
        formContext.setValue('firstDriver.mobile' , '');
        formContext.setValue('firstDriver.gender' , '');

        let productAffiliatedQuery;
        if(rentalType === RentalType.CORPORATE) {
            productAffiliatedQuery = queryClient.getQueryData<IGetCompanyReturn>([ReactQueryKey.Product, {segment: rentalType}]);

            if (productAffiliatedQuery?.firstInvoiceRecipient !== FirstInvoiceRecipient.COMPANY &&
                productAffiliatedQuery?.creditType === CreditType.NO_CREDIT &&
                formContext.getValues('invoice.type') === InvoiceType.PERSON) {
                for (const [key, value] of Object.entries(clearFieldInvoice())) {
                    // @ts-ignore
                    formContext.setValue((`${key}`), value);
                }
            }
        }
        setBirthNationDisabled(false);
    }, []);

    /**
     * Select place from api google
     */
    const selectedBirthCountry = useCallback((result : any) => {
        const country = result?.address_components.find((elem: any) => elem.types.includes('country'));
        const locality = result?.address_components.find((elem: any) => elem?.types.includes('locality'));

        formContext.setValue('firstDriver.birthNation',  country?.short_name ?? '');
        formContext.setValue('firstDriver.birthPlace',  locality?.short_name ?? '');

        setBirthNationDisabled(!!country);
    },[]);

    const googleInputPropsBirthField = {
        onPlaceSelected: (place: any) => selectedBirthCountry(place),
        options: {
            fields: ['address_components']
        }
    }

    /**
     * Reset field birth nation
     */
    const handleResetBirthNation = () => {
        formContext.setValue('firstDriver.birthNation', '');
        setBirthNationDisabled(true);
    }

    return (
        <>
            <DialogSearchCustomer
                section={'firstDriver'}
                open={searchDialog.open}
                onClose={searchDialog.handleClose}
                onCustomerSelected={handleCustomerSelected}
                customersFilter={firstDriverFilter}
            />
            <Accordion expanded={firstDriverExpanded} onChange={(e, expanded) => setFirstDriverExpanded(expanded)}>
                <AccordionSummary expandIcon={<ExpandMore />} sx={{
                    '.MuiAccordionSummary-content': {
                        justifyContent: "space-between",
                        alignItems: "center"
                    }
                }} >
                    <Stack
                        direction="row"
                        alignItems="center"
                        columnGap={2}
                    >
                        <Typography variant="h5">
                            {t('label.mainDriver')}
                        </Typography>
                        {firstDriverId &&
                            <Person
                                color="secondary"
                            />
                        }
                    </Stack>
                    <Stack
                        direction="row"
                        alignItems="center"
                        mr={2}
                    >
                        <IconButton
                            disabled={isPendingBookingRce}
                            onClick={handleSearchClick}
                        >
                            <SearchRounded fontSize="small" />
                        </IconButton>
                        <IconButton
                            onClick={handleDeleteClick}
                            disabled={isPendingBookingRce}
                        >
                            <DeleteRounded fontSize="small" />
                        </IconButton>
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container rowSpacing={1} columnSpacing={3}>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="firstDriver.name"
                                label={t('label.firstName')}
                                required
                                disabled={!!firstDriverId}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="firstDriver.surname"
                                label={t('label.lastName')}
                                required
                                disabled={!!firstDriverId}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <BirthDateField />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {/* Api google country and locality birth */}
                            <Controller
                                name="firstDriver.birthPlace"
                                control={formContext.control}
                                render={({field: {onChange, value}}: any) => (
                                    <GoogleAutocompleteWrapper
                                        googleInputProps={googleInputPropsBirthField}
                                        inputProps={
                                            {
                                                fullWidth: true,
                                                label: t('label.cityOfBirth'),
                                                id: 'birthPlace',
                                                name: 'firstDriver.birthPlace',
                                                type: 'text',
                                                value: value || '',
                                                onChange: (e) => {
                                                    onChange(e.target.value)
                                                    if(!e.target.value) {
                                                        handleResetBirthNation()
                                                    }
                                                },
                                                disabled: !!firstDriverId
                                            }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <BirthNationField birthNationDisabled={birthNationDisabled}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <ResidenceCountryField />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="firstDriver.fiscalCode"
                                label={t('label.fiscalCode')}
                                disabled={!!firstDriverId}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormAutocomplete<IFormValues>
                                name="firstDriver.gender"
                                label={t('label.gender')}
                                options={[
                                    'M',
                                    'F'
                                ]}
                                disabled={!!firstDriverId}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="firstDriver.drivingLicenseNumber"
                                label={t('label.drivingLicenseNumber')}
                                disabled={!!firstDriverId}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormTextField<IFormValues>
                                name="firstDriver.email"
                                label={t('label.email')}
                                required={rentalType === RentalType.RETAIL}
                                disabled={!!firstDriverUUID || isPendingBookingRce}
                            />
                        </Grid>
                        <Grid container item xs={12} sm={6} columnSpacing={1}>
                            <Grid item xs={6} sm={6}>
                                <MobilePrefixField isPendingBookingRce/>
                            </Grid>
                            <Grid item xs={6} sm={6}>
                                <FormTextField<IFormValues>
                                    name="firstDriver.mobile"
                                    label={t('label.mobile')}
                                    disabled={isPendingBookingRce}
                                    required={rentalType === RentalType.RETAIL}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </>
    );
};

export default FirstDriverDetails;
